import React, { useEffect, useState } from "react";
import { Html } from "@react-three/drei";
import { DialogStep, useDialogs } from "../stores/useDialogs";
import { useCameraControl } from "../hooks/useCameraControl";

const C = {
  yellow: '#F7DA8A',
  amber: '#E2A763',
  cream: '#FBF2D3',
  salmon: '#F9A59A',
  dark: '#521616',
  triangle: '#92332E',
  mono: '"IBM Plex Mono", monospace',
};

interface DialogPersonProps {
  npcId: string;
}

export default function DialogPerson({ npcId }: DialogPersonProps) {
  const dialog = useDialogs((state) => state.dialog);
  const closeDialog = useDialogs((state) => state.closeDialog);
  const startFacts = useDialogs((state) => state.startFacts);
  const nextFact = useDialogs((state) => state.nextFact);
  const selectAnswer = useDialogs((state) => state.selectAnswer);
  const nextQuest = useDialogs((state) => state.nextQuest);
  const acceptMapTransition = useDialogs((state) => state.acceptMapTransition);
  const declineMapTransition = useDialogs((state) => state.declineMapTransition);
  const { isTransitioning } = useCameraControl();
  const [keyboardFocusIndex, setKeyboardFocusIndex] = useState(0);

  // Reset keyboard focus when dialog section/step changes
  useEffect(() => {
    setKeyboardFocusIndex(0);
  }, [dialog.sectionIndex, dialog.step]);

  // Keyboard navigation for dialog
  useEffect(() => {
    if (!dialog.isOpen || dialog.npcId !== npcId) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Space / Enter — advance dialog or confirm selected answer
      if (e.code === 'Space' || e.code === 'Enter') {
        e.preventDefault();

        if (dialog.step === DialogStep.FACT) {
          nextFact();
        } else if (dialog.step === DialogStep.QUEST) {
          if (dialog.selectedAnswerIndex === null) {
            // Confirm keyboard-focused answer
            selectAnswer(keyboardFocusIndex);
          } else {
            nextQuest();
          }
        } else if (dialog.step === DialogStep.MAP_UNLOCK_NOTIFICATION) {
          if (dialog.unlockedMapId) {
            acceptMapTransition();
          } else {
            declineMapTransition();
          }
        }
        return;
      }

      // Arrow Up/Down — navigate answers
      if (dialog.step === DialogStep.QUEST && dialog.selectedAnswerIndex === null) {
        const answersCount = dialog.dialogSections[dialog.sectionIndex].answers.length;
        if (e.code === 'ArrowUp') {
          e.preventDefault();
          setKeyboardFocusIndex((prev) => (prev - 1 + answersCount) % answersCount);
          return;
        }
        if (e.code === 'ArrowDown') {
          e.preventDefault();
          setKeyboardFocusIndex((prev) => (prev + 1) % answersCount);
          return;
        }

        // Number keys 1–9 — select answer directly
        const num = parseInt(e.key);
        if (!isNaN(num) && num >= 1 && num <= answersCount) {
          e.preventDefault();
          selectAnswer(num - 1);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [dialog, npcId, keyboardFocusIndex, nextFact, nextQuest, selectAnswer, acceptMapTransition, declineMapTransition]);

  // Only show dialog if it's open and for this specific NPC
  if (!dialog.isOpen || dialog.npcId !== npcId) return null;

  // Helper function to handle answer selection
  const handleAnswerClick = (answerIndex: number) => {
    // If already selected an answer, don't do anything
    if (dialog.selectedAnswerIndex !== null) return;
    
    selectAnswer(answerIndex);
  };

  return (
    <Html position={[0, 1.2, 0]} center>
      <div style={styles.dialogContainer}>
        <div style={styles.dialogHeader}>
          {dialog.npcName || 'Character'}
        </div>

        {dialog.step === DialogStep.FACT && (
          <>
            <div style={styles.dialogContent}>
              <p style={styles.factText}>
                {dialog.dialogSections[dialog.sectionIndex].facts[dialog.factIndex]}
              </p>
              <div style={styles.trianglePointer} />
            </div>
            <div style={styles.buttonContainer}>
              <button onClick={() => nextFact()} style={styles.continueButton}>
                Continue
              </button>
              <button onClick={() => closeDialog()} style={styles.closeButton}>
                Close
              </button>
            </div>
          </>
        )}

        {dialog.step === DialogStep.QUEST && (
          <>
            <div style={styles.dialogContent}>
              <p style={styles.questionText}>
                {dialog.dialogSections[dialog.sectionIndex].question}
              </p>
              <div>
                {dialog.dialogSections[dialog.sectionIndex].answers.map((answer, index) => {
                  const isSelected = dialog.selectedAnswerIndex === index;
                  const correctIndex = dialog.dialogSections[dialog.sectionIndex].correctAnswerIndex;
                  const isCorrect = index === correctIndex;
                  const isKeyboardFocused = dialog.selectedAnswerIndex === null && index === keyboardFocusIndex;

                  return (
                    <button
                      key={index}
                      onClick={() => handleAnswerClick(index)}
                      style={styles.answerButton(
                        isSelected,
                        isCorrect && dialog.selectedAnswerIndex !== null,
                        isKeyboardFocused
                      )}
                      disabled={dialog.selectedAnswerIndex !== null}
                    >
                      {/* Animated shine effect for correct answer */}
                      {isCorrect && dialog.selectedAnswerIndex !== null && (
                        <span style={{
                          position: 'absolute',
                          top: 0,
                          left: '-100%',
                          width: '100%',
                          height: '100%',
                          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                          animation: 'shine 3s infinite',
                        }} />
                      )}
                      {answer}
                    </button>
                  );
                })}
                {/* Show feedback for incorrect answer */}
                {dialog.selectedAnswerIndex !== null &&
                  dialog.selectedAnswerIndex !== dialog.dialogSections[dialog.sectionIndex].correctAnswerIndex && (
                    <div style={styles.incorrectFeedback}>
                      {dialog.dialogSections[dialog.sectionIndex].answerForIncorrect}
                    </div>
                  )}
                {/* Show feedback for correct answer */}
                {dialog.selectedAnswerIndex !== null &&
                  dialog.selectedAnswerIndex === dialog.dialogSections[dialog.sectionIndex].correctAnswerIndex && (
                    <div style={styles.correctFeedback}>
                      The answer is correct!
                    </div>
                  )}
              </div>
              <div style={styles.trianglePointer} />
            </div>
            <div style={styles.buttonContainer}>
              {dialog.selectedAnswerIndex !== null && (
                <button onClick={() => nextQuest()} style={styles.continueButton}>
                  Continue
                </button>
              )}
              <button onClick={() => closeDialog()} style={styles.closeButton}>
                Close
              </button>
            </div>
          </>
        )}

        {dialog.step === DialogStep.MAP_UNLOCK_NOTIFICATION && (
          <>
            {dialog.unlockedMapId ? (
              // Map unlock notification
              <>
                <div style={styles.celebrationContainer}>
                  <div style={styles.celebrationEmoji}>🎉</div>
                  <h3 style={styles.celebrationTitle}>New Map Unlocked!</h3>
                  <p style={styles.celebrationText}>
                    You have unlocked <span style={styles.highlightedText}>{dialog.unlockedMapName}!</span>
                  </p>
                  <p style={styles.celebrationText}>
                    Would you like to travel to the new map now?
                  </p>
                  <div style={styles.trianglePointer} />
                </div>
                <div style={styles.buttonContainer}>
                  <button onClick={() => acceptMapTransition()} style={styles.continueButton}>
                    Yes, let's go!
                  </button>
                  <button onClick={() => declineMapTransition()} style={styles.closeButton}>
                    Stay here
                  </button>
                </div>
              </>
            ) : (
              // Task completion notification
              <>
                <div style={styles.celebrationContainer}>
                  <div style={styles.celebrationEmoji}>🎉</div>
                  <h3 style={styles.celebrationTitle}>Task completed!</h3>
                  <p style={styles.celebrationText}>
                    You have successfully answered all questions!
                  </p>
                  <div style={styles.trianglePointer} />
                </div>
                <div style={styles.buttonContainer}>
                  <button onClick={() => declineMapTransition()} style={styles.closeButton}>
                    Close
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes bounce { from { transform: translateY(0); } to { transform: translateY(-8px); } }
        @keyframes shine { 0% { left: -100%; } 60% { left: 100%; } 100% { left: 100%; } }
      `}</style>
    </Html>
  );
}

// Common styles
const styles = {
  dialogContainer: {
    minWidth: 526,
    maxWidth: 526,
    animation: 'fadeIn 0.3s ease-out',
    position: 'relative' as const,
    paddingTop: '18px',
  },
  dialogHeader: {
    background: C.amber,
    border: `1px solid ${C.dark}`,
    borderRadius: '93px',
    boxShadow: 'inset -3px -3px 0px rgba(212, 28, 28, 0.25)',
    color: C.dark,
    fontFamily: C.mono,
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: 1.5,
    padding: '6px 20px',
    display: 'inline-block',
    position: 'absolute' as const,
    top: 0,
    left: '20px',
    whiteSpace: 'nowrap' as const,
    zIndex: 1,
  },
  dialogContent: {
    background: C.yellow,
    border: `1px solid ${C.dark}`,
    borderRadius: '23px',
    boxShadow: 'inset -3px -3px 0px rgba(212, 28, 28, 0.25)',
    padding: '30px',
    position: 'relative' as const,
  },
  trianglePointer: {
    position: 'absolute' as const,
    right: '12px',
    bottom: '12px',
    width: '26px',
    height: '20px',
    background: C.triangle,
    border: '1px solid #521616',
    boxShadow: 'inset -3px -3px 0px rgba(33, 0, 0, 0.25)',
    borderRadius: '4px',
    transform: 'rotate(-180deg)',
    clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
    zIndex: 1,
  },
  factText: {
    fontFamily: C.mono,
    fontSize: '20px',
    fontWeight: 400,
    color: C.dark,
    lineHeight: 1.5,
    textAlign: 'left' as const,
    margin: '0 0 20px',
  },
  questionText: {
    fontFamily: C.mono,
    fontSize: '20px',
    fontWeight: 400,
    color: C.dark,
    lineHeight: 1.5,
    textAlign: 'left' as const,
    margin: '0 0 20px',
  },
  answerButton: (isSelected: boolean, isCorrect: boolean, isKeyboardFocused?: boolean) => ({
    padding: '12px 16px',
    background: isSelected
      ? (isCorrect ? 'rgba(0, 180, 90, 0.15)' : 'rgba(255, 68, 102, 0.15)')
      : isKeyboardFocused ? 'rgba(82, 22, 22, 0.08)' : C.cream,
    border: `2px solid ${isSelected ? (isCorrect ? '#00b45a' : '#ff4466') : isKeyboardFocused ? C.dark : C.dark}`,
    outline: isKeyboardFocused ? `2px solid ${C.amber}` : 'none',
    outlineOffset: '2px',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.15s',
    fontFamily: C.mono,
    fontSize: '16px',
    fontWeight: isKeyboardFocused ? 500 : 400,
    color: isSelected ? (isCorrect ? '#007a3d' : '#cc2244') : C.dark,
    marginBottom: '10px',
    textAlign: 'left' as const,
    boxShadow: isSelected
      ? (isCorrect ? '0 0 15px rgba(0, 180, 90, 0.25)' : '0 0 15px rgba(255, 68, 102, 0.25)')
      : isKeyboardFocused ? `0 0 0 3px ${C.amber}40` : 'none',
    width: '100%',
    position: 'relative' as const,
    overflow: 'hidden',
    display: 'block',
  }),
  buttonContainer: {
    display: 'flex',
    gap: '12px',
    marginTop: '12px',
  },
  continueButton: {
    background: C.cream,
    border: `1px solid ${C.dark}`,
    borderRadius: '93px',
    boxShadow: 'inset -3px -3px 0px rgba(212, 28, 28, 0.25)',
    color: C.dark,
    fontFamily: C.mono,
    fontSize: '16px',
    fontWeight: 400,
    height: '49px',
    padding: '0 32px',
    cursor: 'pointer',
    flex: 1,
  },
  closeButton: {
    background: C.salmon,
    border: `1px solid ${C.dark}`,
    borderRadius: '23px',
    boxShadow: 'inset -3px -3px 0px rgba(212, 28, 28, 0.25)',
    color: C.dark,
    fontFamily: C.mono,
    fontSize: '16px',
    fontWeight: 400,
    height: '49px',
    padding: '0 32px',
    cursor: 'pointer',
    flex: 1,
  },
  incorrectFeedback: {
    margin: '15px 0 0',
    padding: '12px 15px',
    background: 'rgba(255, 68, 102, 0.1)',
    border: '1px solid #ff4466',
    borderRadius: '10px',
    color: '#cc2244',
    fontFamily: C.mono,
    fontSize: '15px',
    lineHeight: '1.4',
  },
  correctFeedback: {
    margin: '15px 0 0',
    padding: '12px 15px',
    background: 'rgba(0, 180, 90, 0.1)',
    border: '1px solid #00b45a',
    borderRadius: '10px',
    color: '#007a3d',
    fontFamily: C.mono,
    fontSize: '15px',
    lineHeight: '1.4',
  },
  celebrationContainer: {
    background: C.yellow,
    border: `1px solid ${C.dark}`,
    borderRadius: '23px',
    boxShadow: 'inset -3px -3px 0px rgba(212, 28, 28, 0.25)',
    textAlign: 'center' as const,
    padding: '28px 20px 20px',
    position: 'relative' as const,
  },
  celebrationEmoji: {
    fontSize: '40px',
    margin: '0 0 15px',
    animation: 'bounce 1s infinite alternate',
    display: 'inline-block',
  },
  celebrationTitle: {
    margin: '0 0 15px',
    fontSize: '24px',
    fontWeight: 'bold',
    fontFamily: C.mono,
    color: C.dark,
  },
  celebrationText: {
    margin: '0 0 10px',
    fontSize: '16px',
    fontFamily: C.mono,
    color: C.dark,
    lineHeight: '1.5',
  },
  highlightedText: {
    color: C.dark,
    fontWeight: 'bold',
  },
};